import CustomCSSCard from "@/pages/(app)/customer-portal/components/custom-css/custom-css-card";
import { useIsRetractionOnly } from "@/hooks/use-is-retraction-only";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import SettingsFormContainer, {
  SettingsFormHeader,
  SettingsFormSectionHeader,
} from "@babackapp/react-hook-forms-components/form-page";
import useTranslation from "@babackapp/translations/use-translation";
import { CustomMessagesList } from "./components/custom-messages-settings/custom-message-settings-list";
import CustomerPortalBasicsCard from "./components/customer-portal-basics/customer-portal-basics-card";
import DiscountFormCard from "./components/discount-settings/discount-form-card";
import { PortalWordingCard } from "./components/portal-wording/portal-wording-card";
import RefundFormCard from "./components/refund-settings/portal-refund-card";
import PortalExchange from "./components/portal-exchange-form/portal-exchange-card";
import ReturnReasonList from "./components/return-reason-settings/return-reason-list";
import ShopCustomizationCard from "./components/shop-customization/shop-customization-card";
import WidgetPositionCard from "./components/shop-customization/widget-position-card";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { Link } from "react-router-dom";

export default function CustomerPortalPage() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const isRetractionOnly = useIsRetractionOnly();

  return (
    <>
      <SettingsFormContainer
        navigation={[
          {
            title: t("settings.customerPortal.sections.basics.title"),
            id: "basic-informations",
          },
          {
            title: t("settings.config.customerSegments.title"),
            id: "customer-segments",
            locked: isRetractionOnly,
          },
          {
            title: t("settings.customerPortal.sections.customization.title"),
            id: "customization",
          },
          {
            title: t("settings.config.merchandCss"),
            id: "custom-css",
          },
          {
            title: t("settings.customerPortal.sections.exchanges.title"),
            id: "exchanges",
            locked: isRetractionOnly,
          },
          {
            title: t("settings.customerPortal.sections.discounts.title"),
            id: "discounts",
            locked: isRetractionOnly,
          },
          {
            title: t("settings.customerPortal.sections.refunds.title"),
            id: "refunds",
            locked: isRetractionOnly,
          },
          {
            title: t("settings.customerPortal.sections.customMessages.title"),
            id: "custom-messages",
          },
          {
            title: t("settings.customerPortal.sections.portalWording.title"),
            id: "portal-wording",
          },
          {
            title: t("settings.customerPortal.sections.returnReasons.title"),
            id: "return-reasons",
            locked: isRetractionOnly,
          },
        ]}
      >
        <SettingsFormHeader
          title={t("settings.customerPortal.title")}
          description={t("settings.customerPortal.description")}
        />

        <SettingsFormSectionHeader
          title={t("settings.customerPortal.sections.basics.title")}
          id={"basic-informations"}
        />
        <CustomerPortalBasicsCard />

        <SettingsFormSectionHeader
          title={t("settings.customerPortal.sections.customization.title")}
          id={"customization"}
        />
        <WidgetPositionCard />
        <ShopCustomizationCard />

        <SettingsFormSectionHeader
          title={t("settings.config.merchandCss")}
          id={"custom-css"}
        />
        <CustomCSSCard />

        <SettingsFormSectionHeader
          title={t("settings.customerPortal.sections.exchanges.title")}
          id={"exchanges"}
        />
        {isRetractionOnly ? (
          <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
            <div className="pointer-events-none select-none p-6 space-y-4">
              <div className="flex items-center justify-between py-2">
                <div className="space-y-1">
                  <div className="h-3 w-40 rounded bg-muted" />
                  <div className="h-2 w-56 rounded bg-muted/60" />
                </div>
                <div className="h-5 w-9 rounded-full bg-muted" />
              </div>
              <div className="flex flex-col gap-1 py-2">
                <div className="h-3 w-44 rounded bg-muted" />
                <div className="h-2 w-60 rounded bg-muted/60" />
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="space-y-1">
                  <div className="h-3 w-36 rounded bg-muted" />
                  <div className="h-2 w-48 rounded bg-muted/60" />
                </div>
                <div className="h-5 w-9 rounded-full bg-muted" />
              </div>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/70 backdrop-blur-[1.2px] px-6 text-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                <LockClosedIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">Échanges</p>
              <p className="text-xs text-muted-foreground max-w-sm">
                Proposez à vos clients d'échanger leurs articles plutôt que de
                les rembourser.
              </p>
              <Link
                to={`/shops/${shop?.id}/settings/billing`}
                className="flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white shadow-sm"
              >
                <LockClosedIcon className="h-3 w-3" />
                Passer au plan Lite
              </Link>
            </div>
          </div>
        ) : (
          <PortalExchange />
        )}

        <SettingsFormSectionHeader
          title={t("settings.customerPortal.sections.discounts.title")}
          id={"discounts"}
        />
        {isRetractionOnly ? (
          <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
            <div className="pointer-events-none select-none p-6 space-y-4">
              <div className="flex items-center justify-between py-2">
                <div className="space-y-1">
                  <div className="h-3 w-44 rounded bg-muted" />
                  <div className="h-2 w-56 rounded bg-muted/60" />
                </div>
                <div className="h-5 w-9 rounded-full bg-muted" />
              </div>
              <div className="flex flex-col gap-1 py-2">
                <div className="h-3 w-36 rounded bg-muted" />
                <div className="h-8 w-32 rounded bg-muted/60" />
              </div>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/70 backdrop-blur-[1.2px] px-6 text-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                <LockClosedIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">
                Bons de réduction
              </p>
              <p className="text-xs text-muted-foreground max-w-sm">
                Offrez des codes promotionnels lors du retour pour inciter à un
                nouvel achat.
              </p>
            </div>
          </div>
        ) : (
          <DiscountFormCard />
        )}

        <SettingsFormSectionHeader
          title={t("settings.customerPortal.sections.refunds.title")}
          id={"refunds"}
        />
        {isRetractionOnly ? (
          <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
            <div className="pointer-events-none select-none p-6 space-y-4">
              <div className="flex flex-col gap-1 py-2">
                <div className="h-3 w-40 rounded bg-muted" />
                <div className="h-2 w-56 rounded bg-muted/60" />
                <div className="mt-1 flex gap-1">
                  <div className="h-6 w-20 rounded-full bg-muted" />
                  <div className="h-6 w-28 rounded-full bg-muted" />
                </div>
              </div>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/70 backdrop-blur-[1.2px] px-6 text-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                <LockClosedIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">
                Remboursements
              </p>
              <p className="text-xs text-muted-foreground max-w-sm">
                Configurez les règles de remboursement et les tags d'exclusion.
              </p>
            </div>
          </div>
        ) : (
          <RefundFormCard />
        )}

        <SettingsFormSectionHeader
          title={t("settings.customerPortal.sections.customMessages.title")}
          id={"custom-messages"}
        />
        <CustomMessagesList />

        <SettingsFormSectionHeader
          title="Wording du portail"
          id="portal-wording"
        />
        <PortalWordingCard />

        <SettingsFormSectionHeader
          title={t("settings.customerPortal.sections.returnReasons.title")}
          id={"return-reasons"}
        />
        {isRetractionOnly ? (
          <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
            <div className="pointer-events-none select-none p-6 space-y-4">
              {[
                "Taille trop grande",
                "Taille trop petite",
                "Article défectueux",
                "Non conforme à la description",
                "Article non souhaité",
              ].map((label) => (
                <div
                  key={label}
                  className="flex items-center justify-between rounded-md border bg-background px-3 py-2 text-xs"
                >
                  <span className="font-medium">{label}</span>
                  <div className="h-4 w-8 rounded-full bg-muted border" />
                </div>
              ))}
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/70 backdrop-blur-[1.2px] px-6 text-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                <LockClosedIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">
                {t("settings.customerPortal.sections.returnReasons.title")}
              </p>
              <p className="text-xs text-muted-foreground max-w-sm">
                Personnalisez les raisons de retour proposées à vos clients dans
                le portail. Disponible dans le plan Lite.
              </p>
              <Link
                to={`/shops/${shop?.id}/settings/billing`}
                className="flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white shadow-sm"
              >
                <LockClosedIcon className="h-3 w-3" />
                Passer au plan Lite
              </Link>
            </div>
          </div>
        ) : (
          <ReturnReasonList />
        )}
      </SettingsFormContainer>
    </>
  );
}
